import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Eye, MapPin, Clock, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Viewer side: everyone currently sharing with me — map + honest-label list. */
export default function GuardianWatch() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const load = () => api.get("/guardian/watch").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    load();
    const iv = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(iv);
  }, []);

  if (!items) return <p className="text-sm text-zinc-400">Loading…</p>;
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 rounded-2xl px-5 py-10 text-center" data-testid="guardian-watch-empty">
        <Eye className="w-8 h-8 text-zinc-300 mx-auto" />
        <p className="mt-2 text-sm font-700">No one is sharing with you right now.</p>
        <p className="text-xs text-zinc-500 mt-1">When a traveler adds you to a circle and starts a Guardian session, they'll appear here.</p>
      </div>
    );
  }

  const points = items.filter((i) => i.location);
  const center = points.length ? [points[0].location.lat, points[0].location.lon] : [20.59, 78.96];

  return (
    <div data-testid="guardian-watch">
      {points.length > 0 && (
        <div className="rounded-2xl overflow-hidden border border-zinc-200 mb-5" style={{ height: 320 }} data-testid="guardian-watch-map">
          <MapContainer center={center} zoom={points.length === 1 ? 11 : 4} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            {points.map((i) => (
              <CircleMarker key={i.session_id} center={[i.location.lat, i.location.lon]}
                radius={10} pathOptions={{ color: i.location.is_live ? "#059669" : "#f59e0b", fillOpacity: 0.6 }}>
                <Popup>
                  <b>{i.traveler.name}</b><br />
                  {i.location.label}<br />
                  <span style={{ fontSize: 11, color: "#666" }}>Precision: {i.location.precision} · Source: {i.location.source}</span>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* Accessible non-map list */}
      <div className="space-y-3" data-testid="guardian-watch-list">
        {items.map((i) => (
          <div key={i.session_id} className="border border-zinc-200 bg-white rounded-2xl p-4" data-testid="guardian-watch-card">
            <div className="flex items-center gap-3">
              {i.traveler.picture
                ? <img src={i.traveler.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                : <span className="w-10 h-10 rounded-full bg-emerald-600 text-white font-800 inline-flex items-center justify-center">{i.traveler.name[0]?.toUpperCase()}</span>}
              <div className="min-w-0">
                <div className="font-800 text-sm">{i.traveler.name}</div>
                <div className="text-[11px] text-zinc-500">{i.mode.replace("_", " ")} {i.journey_label && `· ${i.journey_label}`} {i.destination && `→ ${i.destination}`}</div>
              </div>
              <span className="ml-auto text-[10px] text-zinc-400 inline-flex items-center gap-1 shrink-0"><Clock className="w-3 h-3" /> until {new Date(i.expires_at).toLocaleString()}</span>
            </div>
            <div className="mt-2.5 text-xs">
              {i.location ? (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-700 ${i.location.is_live ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="guardian-location-label">
                  <MapPin className="w-3 h-3" /> {i.location.label}
                  <span className="font-400 opacity-70">· {i.location.precision === "exact" ? "exact GPS" : i.location.precision === "approx" ? "approximate area" : "city-level"}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-full px-2.5 py-1 font-700" data-testid="guardian-location-none">
                  {i.precision === "status" ? "Status-only sharing — no map location" : "No location received yet"}
                </span>
              )}
              {i.delay_minutes > 0 && <span className="ml-2 text-[10px] text-zinc-400">locations delayed {i.delay_minutes} min by the traveler</span>}
            </div>
            {i.statuses.length > 0 && (
              <div className="mt-2.5 space-y-1" data-testid="guardian-watch-statuses">
                {i.statuses.slice(0, 3).map((st) => (
                  <div key={st.id} className="flex items-center gap-2 text-xs text-zinc-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <b>{st.status_label}</b>{st.status_text && <span>— {st.status_text}</span>}
                    <span className="text-[10px] text-zinc-400">traveler-confirmed · {new Date(st.occurred_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
            <FamilyUpdate sessionId={i.session_id} />
          </div>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-zinc-400">Labels are honest by design: "Live" only within 6 minutes of a device update. Older data is always shown as last-known — never as current.</p>
    </div>
  );
}

function FamilyUpdate({ sessionId }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post(`/guardian/summary/${sessionId}`); setSummary(r.data.summary); }
    catch { toast.error("Summary unavailable right now"); }
    setBusy(false);
  };
  return (
    <div className="mt-3">
      {summary ? (
        <div className="border border-violet-200 bg-violet-50/60 rounded-xl px-3.5 py-2.5 text-xs text-zinc-700" data-testid="guardian-family-update">
          <div className="flex items-center gap-1.5 text-[10px] font-800 uppercase text-violet-700 mb-1"><Sparkles className="w-3 h-3" /> AI family update — from confirmed data only</div>
          {summary}
        </div>
      ) : (
        <button onClick={gen} disabled={busy} data-testid="guardian-family-update-btn"
          className="inline-flex items-center gap-1.5 border border-violet-300 text-violet-700 text-xs font-800 rounded-full px-3.5 py-1.5 hover:bg-violet-50 disabled:opacity-50">
          <Sparkles className="w-3.5 h-3.5" /> {busy ? "Writing update…" : "Generate family update"}
        </button>
      )}
    </div>
  );
}
